import type { CaseCard, CaseEvent, CaseKind, PartRequestCard } from "../../lib/types";

export const KIND_LABEL: Record<CaseKind, string> = {
  warranty: "Garantía",
  repair: "Reparación",
  exchange: "Cambio",
  return: "Devolución",
  other: "Otro",
};

/** What the customer is told: C-0042. */
export const caseNumber = (n: number) => `C-${String(n).padStart(4, "0")}`;

/** A spare-part request, as the customer is told: R-0012. */
export const partRequestNumber = (n: number) => `R-${String(n).padStart(4, "0")}`;

/** "{nombre}" with the customer's first name, or gone with the comma or space before it. */
const fillName = (text: string, name: string | null) => {
  const first = name?.trim().split(/\s+/)[0] ?? "";
  return text.replace(/(,\s*|\s)?\{nombre\}/g, (_m, sep: string | undefined) => (first ? `${sep ?? ""}${first}` : ""));
};

/**
 * A status's suggested message with the case's data. With no name, "{nombre}" goes away
 * with the comma or space before it ("Hola {nombre}." → "Hola.").
 */
export function fillNotice(text: string, c: Pick<CaseCard, "number" | "title" | "model" | "contact" | "product">) {
  return fillName(text, c.contact.name)
    .replaceAll("{caso}", caseNumber(c.number))
    .replaceAll("{producto}", c.product?.name ?? c.model ?? c.title);
}

/** A part request status's suggested message: {repuesto} is the part (and how many). */
export function fillPartNotice(text: string, r: Pick<PartRequestCard, "number" | "partName" | "quantity" | "contact">) {
  return fillName(text, r.contact.name)
    .replaceAll("{solicitud}", partRequestNumber(r.number))
    .replaceAll("{repuesto}", r.quantity > 1 ? `${r.quantity} × ${r.partName}` : r.partName);
}

const FIELD_LABEL: Record<string, string> = {
  kind: "el tipo",
  title: "el título",
  description: "la descripción",
  productId: "el producto",
  model: "el modelo",
  serial: "la serie",
  purchaseDate: "la fecha de compra",
  purchasePlace: "el lugar de compra",
  invoice: "la factura",
  reference: "la referencia interna",
  partId: "el repuesto del catálogo",
  partName: "la pieza",
  quantity: "la cantidad",
  caseId: "el caso",
};

/** When it last changed status (or was opened), from its history. */
export const lastStatusAt = (events: CaseEvent[]) =>
  events.reduce(
    (at, e) => (e.type === "created" || e.type === "status" ? Math.max(at, Date.parse(e.createdAt)) : at),
    0,
  );

/** One line of a case's or a part request's history, in the panel's words. Notes are shown apart. */
export function describeCaseEvent(e: CaseEvent, names: Map<string, string>, what: "case" | "part" = "case") {
  const who = e.actorId ? (names.get(e.actorId) ?? "Alguien") : "El sistema";
  const data = e.data ?? {};
  if (e.type === "created")
    return what === "case"
      ? `${who} abrió el caso en ${String(data.status ?? "el primer estado")}`
      : `${who} registró la solicitud en ${String(data.status ?? "el primer estado")}`;
  const it = what === "case" ? "lo" : "la";
  if (e.type === "status") return `${who} ${it} pasó de ${String(data.from)} a ${String(data.to)}`;
  if (e.type === "assigned")
    return data.to
      ? `${who} se ${it} asignó a ${names.get(String(data.to)) ?? "otra persona"}`
      : `${who} ${it} dejó sin asignar`;
  if (e.type === "edited") {
    const fields = Array.isArray(data.fields) ? data.fields.map((f) => FIELD_LABEL[String(f)] ?? String(f)) : [];
    return fields.length
      ? `${who} cambió ${fields.join(", ")}`
      : `${who} editó ${what === "case" ? "el caso" : "la solicitud"}`;
  }
  return `${who} dejó una nota`;
}
